import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Tooltip,
  Icon,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import {
  FiActivity,
  FiCheckSquare,
  FiRotateCcw,
  FiCheckCircle,
  FiZap,
  FiBook,
  FiShield,
} from 'react-icons/fi'
import {
  ExecuteSettlementTab,
  PaymentConfirmationsTrigger,
  PaymentDetection,
  RefundOperationsTable,
  RefundVerificationTrigger,
  SettlementRecordsTable,
  SettlementVerificationTab,
} from '@/components/paymentManagement'
import { useAdminTabQuery } from '@/navigation/adminTabQuery'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { withNetworkStorageKey } from '@/utils/storageKeys'

const PAYMENT_MANAGEMENT_TABS = [
  'detection',
  'confirmations',
  'refund_operations',
  'refund_verification',
  'execute_settlement',
  'settlement_records',
  'settlement_verification',
] as const

export function PaymentManagementPage() {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const TAB_STORAGE_KEY = useMemo(
    () =>
      withNetworkStorageKey('admin_portal_payment_management_tab', currentEnv),
    [currentEnv]
  )
  const { activeTabIndex: tabIndex, handleTabChange } = useAdminTabQuery({
    tabs: PAYMENT_MANAGEMENT_TABS,
    defaultTab: 'detection',
    storageKey: TAB_STORAGE_KEY,
  })

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <Box>
          <HStack justify="space-between" align="start" mb={2}>
            <Box>
              <Heading size="lg" mb={2}>
                {t('paymentManagement.title')}
              </Heading>
              <Text color="gray.600">{t('paymentManagement.description')}</Text>
            </Box>
          </HStack>
        </Box>

        {/* Payment Management Tabs */}
        <Tabs
          colorScheme="blue"
          variant="enclosed"
          isLazy
          lazyBehavior="keepMounted"
          index={tabIndex}
          onChange={handleTabChange}
        >
          <TabList
            justifyContent={{ base: 'flex-start', md: 'space-between' }}
            overflowX={{ base: 'auto', md: 'visible' }}
            overflowY="hidden"
          >
            <Tooltip
              label={t('paymentManagement.tabs.detection')}
              hasArrow
              placement="top"
            >
              <Tab
                py={2}
                flex={{ base: '0 0 58px', md: 1 }}
                minW={{ base: '58px', md: 0 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                aria-label={t('paymentManagement.tabs.detection')}
              >
                <Icon as={FiActivity} boxSize={6} />
              </Tab>
            </Tooltip>
            <Tooltip
              label={t('paymentManagement.tabs.confirmations')}
              hasArrow
              placement="top"
            >
              <Tab
                py={2}
                flex={{ base: '0 0 58px', md: 1 }}
                minW={{ base: '58px', md: 0 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                aria-label={t('paymentManagement.tabs.confirmations')}
              >
                <Icon as={FiCheckSquare} boxSize={6} />
              </Tab>
            </Tooltip>
            <Tooltip
              label={t('paymentManagement.tabs.refundOperations')}
              hasArrow
              placement="top"
            >
              <Tab
                py={2}
                flex={{ base: '0 0 58px', md: 1 }}
                minW={{ base: '58px', md: 0 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                aria-label={t('paymentManagement.tabs.refundOperations')}
              >
                <Icon as={FiRotateCcw} boxSize={6} />
              </Tab>
            </Tooltip>
            <Tooltip
              label={t('paymentManagement.tabs.refundVerification')}
              hasArrow
              placement="top"
            >
              <Tab
                py={2}
                flex={{ base: '0 0 58px', md: 1 }}
                minW={{ base: '58px', md: 0 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                aria-label={t('paymentManagement.tabs.refundVerification')}
              >
                <Icon as={FiCheckCircle} boxSize={6} />
              </Tab>
            </Tooltip>
            <Tooltip
              label={t('paymentManagement.tabs.executeSettlement')}
              hasArrow
              placement="top"
            >
              <Tab
                py={2}
                flex={{ base: '0 0 58px', md: 1 }}
                minW={{ base: '58px', md: 0 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                aria-label={t('paymentManagement.tabs.executeSettlement')}
              >
                <Icon as={FiZap} boxSize={6} />
              </Tab>
            </Tooltip>
            <Tooltip
              label={t('paymentManagement.tabs.settlementTable')}
              hasArrow
              placement="top"
            >
              <Tab
                py={2}
                flex={{ base: '0 0 58px', md: 1 }}
                minW={{ base: '58px', md: 0 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                aria-label={t('paymentManagement.tabs.settlementTable')}
              >
                <Icon as={FiBook} boxSize={6} />
              </Tab>
            </Tooltip>
            <Tooltip
              label={t('paymentManagement.tabs.settlementVerification')}
              hasArrow
              placement="top"
            >
              <Tab
                py={2}
                flex={{ base: '0 0 58px', md: 1 }}
                minW={{ base: '58px', md: 0 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
                aria-label={t('paymentManagement.tabs.settlementVerification')}
              >
                <Icon as={FiShield} boxSize={6} />
              </Tab>
            </Tooltip>
          </TabList>

          <TabPanels>
            {/* Payment Detection Tab */}
            <TabPanel p={0}>
              <PaymentDetection />
            </TabPanel>

            {/* Payment Confirmations Tab */}
            <TabPanel p={0}>
              <PaymentConfirmationsTrigger />
            </TabPanel>

            {/* Refund Operations Tab */}
            <TabPanel p={0}>
              <RefundOperationsTable />
            </TabPanel>

            {/* Refund Verification Tab */}
            <TabPanel p={0}>
              <RefundVerificationTrigger />
            </TabPanel>

            {/* Execute Settlement Tab */}
            <TabPanel p={0}>
              <ExecuteSettlementTab />
            </TabPanel>

            {/* Settlement Records Tab */}
            <TabPanel p={0}>
              <SettlementRecordsTable />
            </TabPanel>

            {/* Settlement Verification Tab */}
            <TabPanel p={0}>
              <SettlementVerificationTab />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  )
}
